import map from 'lodash/map';
import reduce from 'lodash/reduce';
import uniq from 'lodash/uniq';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import size from 'lodash/size';
import values from 'lodash/values';
import forOwn from 'lodash/forOwn';
import isEqual from 'lodash/isEqual';
import omitBy from 'lodash/omitBy';
import cn from 'classnames';

import PropTypes from 'prop-types';
import React, { Component } from 'react';
import TargetGrid from '~/components/TargetGrid/TargetGrid';
import identify from '~/identify';
import css from './css.css';
import {
    getFieldGrid,
    getFieldGridVisiblitySources,
    getEditFieldValues,
    getItemId,
    getAutocompleteOptions,
    getSubviews,
    getValueAdaptSources,
    getValueAdaptData,
    getColumns,
    getItemRow,
    getFieldGridControlSources,
} from '~/stateGetter';
import TitleRow from './TitleRow';
import {
    presentVisibleEditFields,
    presentAutoCompleteFields,
    presentElements,
    presentFieldConditionSources,
    reduceFieldSources,
    presentColumnFields,
    presentVisibleGrid,
} from '~/statePresenter';
import { EditField, AutocompleteField } from '~/ui/Fields/VariadicField/VariadicField';
import { compareKeys, modify } from 'mythil/object';
import { connect } from '~/redux/connect';

import Subview from '~/ui/Subview/Subview';
import { iterateGridTargets, iterateGridElements } from '~/data/targetGrid';
import toOrderedArray from '~/lib/toOrderedArray';
import { TimeCounter } from '~/components/TimeCounter/TimeCounter';
import { ReadonlyField, Row } from '~/native-ui';
import { WidgetRow } from './WidgetRow';


const ToolTippedReadonlyField = ({ ...props }) => {
    return <ReadonlyField {...props} />;
};

const Counter = ({ value, interval, timerDueText, ...props }) => {
    if (!value) {
        return null;
    }

    return (
        <ToolTippedReadonlyField
            {...props}
            renderAsHtml={false}
            value={<TimeCounter interval={interval} dueText={timerDueText} date={value} />}
        ></ToolTippedReadonlyField>
    );
};

class ColumnField extends Component {
    renderCounter() {
        return <Counter {...this.props}></Counter>;
    }

    render() {
        const { interval, timerDueText, ...props } = this.props;

        if (interval && props.value) {
            return this.renderCounter();
        }

        return <ToolTippedReadonlyField {...props} />;
    }
}

const elementMapping = {
    editField: { type: EditField },
    subview: { type: Subview },
    columnField: { type: ColumnField, pass: true },
};

const rowStyleProps = ["rowTheme"];
const emptyObject = {};

class ItemGrid extends Component {
    static propTypes = {
        grid: PropTypes.object,
        elements: PropTypes.object,
        autotheme: PropTypes.bool,
    };

    static defaultProps = {
        maxLevel: null,
    };

    presentRowTitleRow(row, hasNextGroup) {
        return [{ element: <TitleRow modelId={this.props.modelId} row={row} /> }];
    }

    prepareWidgetRow(nodes, row) {
        return [{
            element: (
                <WidgetRow
                    modelId={this.props.modelId}
                    {...row}
                    nodes={nodes}
                />
            )
        }];
    }

    renderElements = () => {
        let { elements, modelId } = this.props;
        let renderedElements = {};

        forOwn(elements, (elements, elementType) => {
            return forOwn(elements, (fields, target) => {
                return fields.forEach((field) => {
                    let { name, group } = field;

                    if (!name) {
                        throw new Error('No name provided for element in ' + target);
                    }

                    let elementData = elementMapping[elementType],
                        Element = elementData.type,
                        pass = elementData.pass;

                    let preparedElement = {
                        group,
                        name,
                        element: pass ? (
                            <Element {...field} view={this.props.view} />
                        ) : (
                            <Element modelId={modelId} name={name} view={this.props.view} />
                        ),
                        elementType,
                    };

                    if (!(group in renderedElements)) {
                        renderedElements[group] = [];
                    }

                    renderedElements[group].push(preparedElement);
                });
            });
        });

        return renderedElements;
    };

    computeElementClassNames = (elements) => {
        if (!elements) {
            return null;
        }

        let classNames = {};

        for (let elementData of elements) {
            classNames[css[elementData.elementType + 'Row']] = true;
        }

        return cn(classNames);
    };

    isRowVisible = (row, i, elementsAccumulator) => {
        let name = row.name ? row.name : i;
        let found = false;

        iterateGridTargets([row], (targetName) => {
            if (targetName in elementsAccumulator) {
                found = true;
                return false;
            }
        });

        return found;
    };

    // getLevel = (level) => {
    //     let { maxLevel } = this.props;

    //     if (this.props.maxLevel !== null) {
    //         return Math.min(maxLevel, level);
    //     }

    //     return level;
    // };

    // capLevel = (obj) => {
    //     if (obj.level) {
    //         obj = modify(obj, 'level', this.getLevel);
    //     }

    //     if (obj.style && obj.style.level) {
    //         obj = modify(obj, 'style.level', this.getLevel);
    //     }

    //     return obj;
    // };


    deriveRootRowTheme = (obj) => {
        let rowCount = 0, theme;
        iterateGridElements(obj, (nodeType, node) => {
            if (nodeType === 'row')  {
                rowCount++;
                theme = node.rowTheme || theme;
            }

            if (rowCount >= 2) return false;
        });

        return theme === "dark" ? "light" : "dark";
    }

    prepareRow = (row, elementsAccumulator, hasNextGroup) => {
        let name = row.name;


        if (row.type === "widget") {
            return this.prepareWidgets(row, elementsAccumulator);
        }

        if (name) {
            row.rowClassName = cn(row.rowClassName, this.computeElementClassNames(elementsAccumulator[row.name]));
        } else {
            row.cols = row.cols.map((col) => {
                return {
                    ...col,
                    rows: this.prepareRows(col.rows, elementsAccumulator, hasNextGroup),
                };
            });
        }

        if (row.title && row.title.length) {
            name = row.name || row.key;
            elementsAccumulator[identify.titleRowIdentifier(name)] = this.presentRowTitleRow(row, hasNextGroup);

            let subRow = omit(row, rowStyleProps),
                styleProps = pick(row, rowStyleProps);

            row = {
                ...styleProps,
                xs: 12,
                cols: [
                    {
                        xs: 12,
                        rows: [
                            {
                                name: identify.titleRowIdentifier(name),
                                xs: 12,
                                elementColClassName: css.titleCol,
                                rowProps: {},
                            },
                            subRow,
                        ],
                    },
                ],
            };
        }

        return row;
    };

    renderWidgetGroup = (widgetGroupNode) => {
        const theme = this.deriveRootRowTheme(this.props.grid);
        return <Row rowTheme={theme} borderWidth="0" boxShadow="none">{widgetGroupNode}</Row>;
    }

    prepareWidgetGroup = (widgetGroup, elementsAccumulator) => {
        const nodes = toOrderedArray(widgetGroup.nodes).map((tab) => this.prepareWidget(tab, elementsAccumulator));
        return ({
            isWidgetGroup: true,
            title: widgetGroup.title,
            description: widgetGroup.description,
            key: widgetGroup.key,
            nodes,
            renderGroup: this.renderWidgetGroup
        });
    }

    prepareWidget = (widget, elementsAccumulator) => {
        const children = this.prepareRows(widget.rows, elementsAccumulator, { borderRadius: 0, borderWidth: 0, boxShadow: "none" });
        return ({
            title: widget.title,
            icon: widget.icon,
            iconFamily: widget.iconFamily,
            key: widget.key,
            description: widget.description,
            disabled: widget.disabled,
            children: !size(children) ? null
                : <TargetGrid
                    rows={children} 
                    maxLevel={this.props.maxLevel}
                    elements={elementsAccumulator}
                    className={this.props.className}
                />
        })
    }


    prepareWidgets(row = {}, elementsAccumulator) {
        const styleProps = pick(row, rowStyleProps);

        const preparedWigetNodes = toOrderedArray(row.nodes ?? []).map((tab) => {
            if (tab.nodes) return this.prepareWidgetGroup(tab, elementsAccumulator);
            return this.prepareWidget(tab, elementsAccumulator);
        });

        elementsAccumulator[identify.tabRowIdentifier(row.uniqueKey || row.key)] = this.prepareWidgetRow(preparedWigetNodes, row);

        return {
            ...styleProps,
            ...(row.style ?? {}),
            pt: "0",
            xs: 12,
            cols: [{
                xs: 12,
                rows: [{
                    name: identify.tabRowIdentifier(row.uniqueKey || row.key),
                    xs: 12,
                }]
            }],
        }
    }

    prepareRows(rows, elementsAccumulator, defaultStyles = {}) {
        let preparedRows = [];
        let rowClassName = this.props.view === identify.VIEW_EDIT ? css.editRow : css.readonlyRow;

        rows = toOrderedArray(rows);

        for (let i = 0; i < rows.length; i++) {
            let row = rows[i],
                hasNextGroup = rows.length >= i + 1;

            if (!this.isRowVisible(row, i, elementsAccumulator)) {
                continue;
            }

            row = { ...row, ...defaultStyles, rowClassName };
            row = this.prepareRow(row, elementsAccumulator, hasNextGroup);

            preparedRows.push(row);
        }

        return preparedRows;
    }

    renderShadowFields() {
        let { modelId, autocompleteFields } = this.props;

        return (
                map(autocompleteFields, (field, index) => (
                    <AutocompleteField
                        index={index}
                        key={field.name}
                        modelId={modelId}
                        name={field.name}
                        isReadonly={true}
                        view={this.props.view}
                        inputType={identify.EDITFIELD}
                    />
                ))
        );
    }

    renderGrid() {
        let elements = this.renderElements();
        let rows = this.prepareRows(this.props.grid.rows, elements);

        const grid = (
            <TargetGrid
                key="the_grid_itself"
                maxLevel={this.props.maxLevel}
                rows={rows}
                elements={elements}
                className={this.props.className}
            />
        );

        if (!this.props.autotheme) return grid;

        const theme = this.deriveRootRowTheme(this.props.grid);

        return <Row rowTheme={theme} borderRadius="0" borderWidth="0" boxShadow="none">{grid}</Row>;
    }

    render() {

        return (
            <React.Fragment key="item_grid">
                {this.renderShadowFields()}
                {this.renderGrid()}
            </React.Fragment>
        );
    }
}

const areStatePropsEqual = (prev, curr) => {
    // let t1 = performance.now();

    let isEqual = _areStatePropsEqual(prev, curr);

    // let t2 = performance.now();

    // if(!isEqual) {
    // console.log('GRID areStatePropsEqual PERF '+(t2 - t1), diffKeys(prev, curr));
    // }

    return isEqual;
};

const _areStatePropsEqual = (props, prevProps) => {
    return compareKeys(props, prevProps, (prop, prevProp, key) => {
        switch (key) {
            case 'valueAdaptSources':
            case 'editFields':
                return isEqual(prop, prevProp);
            default:
                return prop === prevProp;
        }
    });
};

// deep equal is cheaper than reconcilation
const areMergedPropsEqual = (props, prevProps) => {
    return isEqual(props, prevProps);
};

// !!ATTENTION!! THIS IS PERFORMANCE-CRITICAL, MAKE SURE THAT REDUX-CONNECT CACHING STAYS INTACT
// TODO: OPTIMIZE PERFORMANCE CURRENTLY ON MBP 2012 RECONCILATION OF GRID TAKES ~100-200 ms of
const RXItemGrid = connect(
    (state, { modelId, view, enableSubviews = true, gridName = 'default' }) => {
        let props = getValueAdaptData(state, modelId);

        const controlSources = getFieldGridControlSources(state, modelId, gridName).sources;
        const editFields = reduce(props.editFields, (fields, field) => {
            return { ...fields, [field.name]: field };
        }, {});

        let { valueKeys, optionKeys } = presentFieldConditionSources(editFields);

        valueKeys = Array.from(new Set([...valueKeys,...getFieldGridVisiblitySources(state, modelId, gridName)]));

        let valueAdaptFields = values(pick(editFields, uniq([...valueKeys, ...optionKeys])));
        let conditionValues = pick(getEditFieldValues(state, modelId), valueKeys);
        props = { ...props, ...getValueAdaptData(state, modelId) };
        props.valueAdaptSources = getValueAdaptSources(state, modelId);
        props.valueAdaptSources = reduceFieldSources(
            valueAdaptFields,
            editFields,
            props.filters,
            props.valueAdaptSources
        );
        props.valueAdaptSources.editFieldValues = { ...props.valueAdaptSources.editFieldValues, ...conditionValues };

        let conditionAutocompletes = pick(getAutocompleteOptions(state, modelId, identify.EDITFIELD), optionKeys);
        props.valueAdaptSources.editFieldOptions = {
            ...props.valueAdaptSources.editFieldOptions,
            ...conditionAutocompletes,
        };

        let itemId = getItemId(state, modelId);
        let subviews = enableSubviews && itemId && itemId !== identify.NEW_ITEM ? getSubviews(state, modelId) : emptyObject;

        let grid = getFieldGrid(state, modelId, gridName);

        return {
            ...props,
            editFields,
            controlSources,
            subviews,
            grid,
            view,
            itemId: getItemId(state, modelId),
            columns: getColumns(state, modelId),
            itemRow: getItemRow(state, modelId),
        };
    },
    (dispatch) => ({}),
    (props, actionProps, { modelId, ...ownProps }) => {
        const b = 4;

        let { subviews, valueAdaptSources, editFields, columns, itemRow, controlSources, ...other } = props;

        let visibleEditFields = presentVisibleEditFields({
            ...valueAdaptSources,
            ...props,
        });

        let autocompleteFields = presentAutoCompleteFields({
            ...valueAdaptSources,
            ...props,
        });

        let columnField = {};

        if (itemRow) {
            columnField = presentColumnFields({ columns, itemRow });
        }

        let elements = presentElements({
            editField: visibleEditFields,
            subview: subviews,
            columnField,
        });

        return {
            ...actionProps,
            ...other,
            ...ownProps,
            view: props.view,
            autocompleteFields,
            elements,
            modelId,
            grid: presentVisibleGrid({
                ...valueAdaptSources,
                ...props,
            })
        };
    },
    { areStatePropsEqual, areMergedPropsEqual }
)(ItemGrid);

export default RXItemGrid;
